
import { RecommendedCareer } from '@/types/assessment';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface CareerRecommendationsProps {
  recommendations?: RecommendedCareer[];
}

const CareerRecommendations = ({ recommendations }: CareerRecommendationsProps) => {
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  // Sort recommendations by score (highest first)
  const sortedRecommendations = [...recommendations].sort((a, b) => b.score - a.score);
  
  // Take top 5 recommendations
  const topRecommendations = sortedRecommendations.slice(0, 5);

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="text-xl text-brand-blue">Recommended Career Paths</CardTitle>
        <CardDescription>
          Based on your assessment, these career paths may be suitable for you
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {topRecommendations.map((career) => (
            <AccordionItem key={career.categoryId} value={`career-${career.categoryId}`}>
              <AccordionTrigger className="text-left hover:no-underline">
                <div className="flex items-center">
                  <span className="font-medium">{career.categoryName}</span>
                  <Badge className="ml-3 bg-brand-blue/90" variant="default">
                    {Math.round(career.score)}% Match
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {career.subcategories && career.subcategories.length > 0 ? (
                  <div className="mt-2">
                    <h4 className="text-sm font-semibold mb-2">Recommended Specializations:</h4>
                    <ul className="list-disc pl-6 space-y-1">
                      {career.subcategories.map((subcareer, index) => (
                        <li key={index} className="text-sm text-gray-700">
                          {subcareer}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 italic">
                    Explore general career opportunities in this field.
                  </p>
                )}
                
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    Your assessment results show a strong alignment with this career path. 
                    Consider researching education requirements and job opportunities in this field.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default CareerRecommendations;
